<template>
  <div class="timeline-block">
    <div v-if="displayEvents.length === 0 && preview" class="block-preview-empty">No events to display</div>
    <div v-else-if="displayEvents.length > 0" class="timeline">
      <!-- Happening Now (ongoing multi-day + today's single-day events) -->
      <template v-if="ongoingEvents.length > 0 || todayEvents.length > 0">
        <div class="timeline-section-label ongoing-label"><span>Happening Now</span></div>
        <div v-for="event in [...ongoingEvents, ...todayEvents]" :key="event.id" class="timeline-event ongoing">
          <div class="event-date-badge" :class="{ 'multi-day': isMultiDay(event) }">
            <template v-if="!isMultiDay(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
            </template>
            <template v-else-if="isSameMonth(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
            </template>
            <template v-else-if="!isCrossYear(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-month">{{ formatMonth(event.endDate) }}</span>
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.endDate) }}</span>
            </template>
            <template v-else>
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-month">{{ formatMonth(event.endDate) }}</span>
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.endDate) }}</span>
            </template>
          </div>
          <div class="event-card" :class="{ 'has-thumbnail': event.thumbnail }">
            <div class="event-card-body">
              <h3 class="event-title">{{ event.title }}</h3>

              <p v-if="event.location" class="event-location">{{ event.location }}</p>
              <ReadMore v-if="event.description" :text="event.description" :threshold="350" />
              <a v-if="event.link" :href="event.link" class="event-link" target="_blank" rel="noopener">{{
                event.linkText ||
                'Learn more' }} &rarr;</a>
            </div>
            <img v-if="event.thumbnail" :src="event.thumbnail" :alt="event.title" class="event-thumbnail" loading="lazy">
          </div>
        </div>
      </template>

      <!-- Upcoming Events -->
      <template v-if="upcomingEvents.length > 0 && showUpcoming">
        <div class="timeline-section-label upcoming-label"><span>Upcoming</span></div>
        <div v-for="event in upcomingEvents" :key="event.id" class="timeline-event upcoming">
          <div class="event-date-badge" :class="{ 'multi-day': isMultiDay(event) }">
            <template v-if="!isMultiDay(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
            </template>
            <template v-else-if="isSameMonth(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
            </template>
            <template v-else-if="!isCrossYear(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-month">{{ formatMonth(event.endDate) }}</span>
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.endDate) }}</span>
            </template>
            <template v-else>
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-month">{{ formatMonth(event.endDate) }}</span>
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.endDate) }}</span>
            </template>
          </div>
          <div class="event-card" :class="{ 'has-thumbnail': event.thumbnail }">
            <div class="event-card-body">
              <h3 class="event-title">{{ event.title }}</h3>

              <p v-if="event.location" class="event-location">{{ event.location }}</p>
              <ReadMore v-if="event.description" :text="event.description" :threshold="350" />
              <a v-if="event.link" :href="event.link" class="event-link" target="_blank" rel="noopener">{{
                event.linkText ||
                'Learn more' }} &rarr;</a>
            </div>
            <img v-if="event.thumbnail" :src="event.thumbnail" :alt="event.title" class="event-thumbnail" loading="lazy">
          </div>
        </div>
      </template>

      <!-- Past Events -->
      <template v-if="pastEvents.length > 0 && showPast">
        <div class="timeline-section-label past-label"><span>Past</span></div>
        <div v-for="event in pastEvents" :key="event.id" class="timeline-event past">
          <div class="event-date-badge" :class="{ 'multi-day': isMultiDay(event) }">
            <template v-if="!isMultiDay(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
            </template>
            <template v-else-if="isSameMonth(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
            </template>
            <template v-else-if="!isCrossYear(event)">
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-month">{{ formatMonth(event.endDate) }}</span>
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.endDate) }}</span>
            </template>
            <template v-else>
              <span class="event-month">{{ formatMonth(event.date) }}</span>
              <span class="event-day">{{ formatDay(event.date) }}</span>
              <span class="event-year">{{ formatYear(event.date) }}</span>
              <span class="badge-separator" />
              <span class="event-month">{{ formatMonth(event.endDate) }}</span>
              <span class="event-day">{{ formatDay(event.endDate) }}</span>
              <span class="event-year">{{ formatYear(event.endDate) }}</span>
            </template>
          </div>
          <div class="event-card" :class="{ 'has-thumbnail': event.thumbnail }">
            <div class="event-card-body">
              <h3 class="event-title">{{ event.title }}</h3>

              <p v-if="event.location" class="event-location">{{ event.location }}</p>
              <ReadMore v-if="event.description" :text="event.description" :threshold="350" />
              <a v-if="event.link" :href="event.link" class="event-link" target="_blank" rel="noopener">{{
                event.linkText ||
                'Learn more' }} &rarr;</a>
            </div>
            <img v-if="event.thumbnail" :src="event.thumbnail" :alt="event.title" class="event-thumbnail" loading="lazy">
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ReadMore from '@/components/ReadMore.vue'

export default {
  name: 'TimelineBlock',
  components: { ReadMore },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return { now: today }
  },
  computed: {
    showPast() {
      return this.data.showPast !== false
    },
    showUpcoming() {
      return this.data.showUpcoming !== false
    },
    events() {
      return (this.data.events || []).map(event => ({
        ...event,
        link: this.isValidHttpUrl(event.link) ? event.link : '',
        _start: this.parseLocalDate(event.date),
        // endDate falls back to start date (single-day event)
        _end: event.endDate?.trim() ? this.parseLocalDate(event.endDate) : this.parseLocalDate(event.date)
      }))
    },
    ongoingEvents() {
      // Multi-day event: started on or before today AND ends on or after today
      return this.events
        .filter(e => e._start <= this.now && e._end >= this.now && e.endDate?.trim() && e.endDate !== e.date)
        .sort((a, b) => a._start - b._start)
    },
    todayEvents() {
      // Single-day events happening today (start === today, no endDate or endDate === date)
      return this.events
        .filter(e => e._start.getTime() === this.now.getTime() && (!e.endDate?.trim() || e.endDate === e.date))
        .sort((a, b) => a._start - b._start)
    },
    upcomingEvents() {
      // Starts in the future (after today)
      return this.events
        .filter(e => e._start > this.now)
        .sort((a, b) => a._start - b._start)
    },
    pastEvents() {
      // Already ended (endDate < today, or single-day event before today)
      return this.events
        .filter(e => e._end < this.now)
        .sort((a, b) => b._start - a._start)
    },
    displayEvents() {
      const result = [...this.ongoingEvents, ...this.todayEvents]
      if (this.showUpcoming) result.push(...this.upcomingEvents)
      if (this.showPast) result.push(...this.pastEvents)
      return result
    }
  },
  mounted() {
    // Refresh `now` at midnight so categorization stays correct for long-lived pages
    const msToMidnight = () => {
      const t = new Date(); t.setHours(24, 0, 0, 0); return t - Date.now()
    }
    const schedule = () => {
      this._midnightTimer = setTimeout(() => {
        const d = new Date(); d.setHours(0, 0, 0, 0)
        this.now = d
        schedule()
      }, msToMidnight())
    }
    schedule()
  },
  beforeUnmount() {
    clearTimeout(this._midnightTimer)
  },
  methods: {
    isMultiDay(event) {
      return event.endDate?.trim() && event.endDate !== event.date
    },
    isCrossYear(event) {
      if (!this.isMultiDay(event)) return false
      return this.parseLocalDate(event.date).getFullYear() !== this.parseLocalDate(event.endDate).getFullYear()
    },
    isSameMonth(event) {
      if (!this.isMultiDay(event)) return true
      const s = this.parseLocalDate(event.date)
      const e = this.parseLocalDate(event.endDate)
      return s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()
    },
    parseLocalDate(dateStr) {
      if (!dateStr) return new Date(0)
      const [y, m, d] = dateStr.split('-').map(Number)
      return new Date(y, m - 1, d)
    },
    formatMonth(dateStr) {
      return this.parseLocalDate(dateStr).toLocaleString('en-US', { month: 'short' }).toUpperCase()
    },
    formatDay(dateStr) {
      return this.parseLocalDate(dateStr).getDate()
    },
    formatYear(dateStr) {
      return this.parseLocalDate(dateStr).getFullYear()
    },
  }
}
</script>

<style scoped>
.block-preview-empty {
  padding: 12px 16px;
  color: var(--color-muted, #999);
  font-size: 0.85rem;
  font-style: italic;
}

.timeline-block {
  width: 100%;
  padding: 0;
}


.timeline-section-label {
  font-family: var(--font-primary);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: color-mix(in srgb, var(--color-text) 15%, var(--color-bg));
  }

  span {
    display: inline-block;
    padding: 4px 14px;
    border-radius: 999px;
  }

  &.ongoing-label span {
    background-color: var(--color-highlight);
    color: var(--color-highlight-text, #fff);
  }

  &.ongoing-label::after {
    background-color: var(--color-highlight);
  }

  &.upcoming-label {
    margin-top: 32px;
  }

  &.upcoming-label span {
    background-color: var(--color-accent);
    color: var(--color-accent-text, #fff);
  }

  &.upcoming-label::after {
    background-color: color-mix(in srgb, var(--color-accent) 40%, transparent);
  }

  &.past-label {
    margin-top: 32px;
  }

  &.past-label span {
    background-color: color-mix(in srgb, var(--color-text) 65%, var(--color-bg));
    color: var(--color-bg);
  }
}

.timeline-event {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: flex-start;

  &.past {
    opacity: 0.85;
  }
}

.event-date-badge {
  flex-shrink: 0;
  width: 70px;
  text-align: center;
  padding: 12px 8px;
  border-radius: 6px;
  background-color: color-mix(in srgb, var(--color-text) 8%, var(--color-bg));
  border: 1px solid color-mix(in srgb, var(--color-text) 12%, var(--color-bg));

  .ongoing & {
    background-color: var(--color-highlight);
    border-color: var(--color-highlight);
  }

  .upcoming & {
    background-color: var(--color-accent);
    border-color: var(--color-accent);
  }

  &.multi-day {
    padding: 8px 6px;
  }
}

.badge-separator {
  display: block;
  font-family: var(--font-primary);
  font-size: 11px;
  letter-spacing: 1px;
  color: color-mix(in srgb, var(--color-text) 60%, transparent);
  margin: 2px 0;

  .ongoing & { color: color-mix(in srgb, var(--color-highlight-text) 60%, transparent); }
  .upcoming & { color: color-mix(in srgb, var(--color-accent-text) 60%, transparent); }

  &::after {
    content: '–';
  }
}


.event-month {
  display: block;
  font-family: var(--font-primary);
  font-size: 13px;
  letter-spacing: 1px;
  color: color-mix(in srgb, var(--color-text) 75%, transparent);

  .ongoing & { color: color-mix(in srgb, var(--color-highlight-text) 85%, transparent); }
  .upcoming & { color: color-mix(in srgb, var(--color-accent-text) 85%, transparent); }
}

.event-day {
  display: block;
  font-family: var(--font-primary);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--color-text);

  .multi-day & { font-size: 20px; }
  .ongoing & { color: var(--color-highlight-text); }
  .upcoming & { color: var(--color-accent-text); }
}

.event-year {
  display: block;
  font-family: var(--font-primary);
  font-size: 13px;
  letter-spacing: 1.5px;
  color: color-mix(in srgb, var(--color-text) 60%, transparent);

  .ongoing & { color: color-mix(in srgb, var(--color-highlight-text) 70%, transparent); }
  .upcoming & { color: color-mix(in srgb, var(--color-accent-text) 70%, transparent); }
}

.event-card {
  flex: 1;
  padding: 16px 20px;
  background: var(--color-bg);
  border: 1px solid color-mix(in srgb, var(--color-text) 12%, var(--color-bg));
  border-radius: 6px;
  text-align: left;

  &.has-thumbnail {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .ongoing & {
    border-left: 3px solid var(--color-highlight);
  }

  .upcoming & {
    border-left: 3px solid color-mix(in srgb, var(--color-accent) 50%, var(--color-bg));
  }
}

.event-thumbnail {
  width: 90px;
  height: 90px;
  object-fit: cover;
  border-radius: var(--radius);
  flex-shrink: 0;
}

.event-card-body {
  flex: 1;
  min-width: 0;
}

.event-title {
  margin: 0 0 6px 0;
  font-family: var(--font-decorative);
  font-size: 18px;
  color: var(--color-text);
}


.event-location {
  margin: 0 0 8px 0;
  font-family: var(--font-primary);
  font-size: 17px;
  color: color-mix(in srgb, var(--color-text) 85%, transparent);
  font-style: italic;
}

.event-card-body :deep(.rm-text) {
  font-family: var(--font-primary);
  font-size: 17px;
  line-height: var(--line-height-base);
  color: color-mix(in srgb, var(--color-text) 90%, transparent);
  margin: 0 0 6px;
}

.event-link {
  display: inline-block;
  margin-top: 4px;
  font-family: var(--font-primary);
  font-size: 15px;
  color: var(--color-highlight);
  text-decoration: none;

  &:hover {
    color: var(--color-highlight-hover);
    text-decoration: underline;
  }
}

/* Mobile; admin previews always show the desktop page */
@media only screen and (max-width: 500px) {
  :where(.timeline-block:not(.preview-page *)) {
    .timeline-event {
      flex-direction: column;
      gap: 8px;
    }

    .event-card {
      width: 100%;
    }

    .event-date-badge {
      width: auto;
      display: flex;
      gap: 6px;
      align-items: baseline;
      padding: 8px 12px;
    }

    .event-month,
    .event-day,
    .event-year {
      display: inline;
      font-size: var(--font-xs);
    }

    .event-day {
      font-size: var(--font-sm);
    }

    .badge-separator {
      display: inline;
      margin: 0 3px;
    }

    .event-thumbnail {
      width: 70px;
      height: 70px;
    }
  }
}
</style>
